import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { quoteText } from "@/lib/quote-cta";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE, absoluteUrl } from "@/config/sites";
import { buildHreflangLinks } from "@/lib/hreflang";
import { SERVICE_CATEGORIES } from "@/data/services";
import { PageHero } from "@/components/PageHero";
import { SERVICES_HOWTO } from "@/data/services-howto-i18n";
import { LocaleDeepContentBlock } from "@/components/LocaleDeepContentBlock";
import { DEEP_CONTENT } from "@/data/locale-deep-content";
import {
  buildJsonLd,
  jsonLdScript,
  organizationSchema,
  webPageSchema,
  breadcrumbListSchema,
  legalServiceSchema,
  faqPageSchema,
  howToSchema,
} from "@/lib/schema";

const TITLE = "Notary Public Thailand — Certified Legalization";
const DESC =
  "Licensed Thai notarial attorneys: notary public, certified translation (7 languages), MFA + embassy. Ask our staff for a quotation.";

const EN_FAQ = [
  {
    q: "How much does a Notary Public in Thailand cost?",
    a: "Our professional fees are quoted per document after staff review the document, destination and number of signatures or pages. Government fees, such as the MFA legalisation fee, are charged at the official rate and listed separately in the quotation. Processing time depends on the receiving authority and is confirmed with the quotation.",
  },
  {
    q: "Are your notaries recognised by embassies and foreign authorities?",
    a: "Yes. All 6 attorneys hold the Notarial Services Attorney licence from the Lawyers Council of Thailand under the Royal Patronage (Regulation 2551 B.E.). Notarised documents are accepted by every embassy in Bangkok, the Thai Ministry of Foreign Affairs (MFA Chaeng Watthana), and foreign courts, universities, and banks worldwide.",
  },
  {
    q: "Can I get certification of copies of my passport, degree, or ID in Thailand?",
    a: "Yes. Certification of copies (Certified True Copy) is one of our most requested services in Bangkok. Common documents include passports, national ID cards, birth certificates, marriage certificates, degree certificates, transcripts, and company registration certificates. Turnaround is same-day; walk-ins accepted at our Bangkok HQ.",
  },
  {
    q: "Do you handle document legalization for use abroad (Apostille / consular chain)?",
    a: "Yes. We handle end-to-end document legalization: Notary Public → MFA legalization → destination embassy legalization. Thailand acceded to the Hague Apostille Convention on 30 June 2026 and it enters into force for Thailand on 28 February 2027; until then destinations still require the full MFA + embassy chain. We advise on the correct chain per country.",
  },
  {
    q: "Do you offer notary services in English, Chinese, Japanese, Korean, Vietnamese, or Indonesian?",
    a: "Yes. Our team supports 7 languages: Thai, English, Chinese (Mandarin), Japanese, Korean, Vietnamese, and Indonesian — for both notarial services and certified translation. This covers the vast majority of international business and personal documentation needs in Thailand.",
  },
  {
    q: "Can the notary come to my hotel, condo, or office in Bangkok?",
    a: "Yes. On-site notarial service is available across all 50 Bangkok districts (Sathorn, Silom, Sukhumvit, Asoke, Phrom Phong, Thonglor, Rama 9, Ratchada, Ladprao, Thonburi, etc.) and greater Bangkok. Book at least one business day ahead via LINE @Thainotary or +66-94-895-8999.",
  },
];

const EN_INTRO =
  "Thai Notary Law & Service Co., Ltd. is a licensed Thai law firm specialising in notarial services, certified translation, and international document legalization for foreign nationals, multinational corporations, and Thai companies dealing with cross-border matters. Founded in Bangkok with branches in Khon Kaen, Udon Thani, and Nong Khai, our team of 6 Notarial Services Attorneys — each licensed under the Lawyers Council of Thailand — has more than 60 years of combined experience serving embassies, universities, foreign courts, and Fortune 500 legal departments.";

const EN_WHY = [
  "6 licensed Notarial Services Attorneys (Lawyers Council of Thailand, Regulation 2551 B.E.)",
  "7 working languages: Thai, English, Chinese, Japanese, Korean, Vietnamese, Indonesian",
  "4 physical branches: Bangkok HQ, Khon Kaen, Udon Thani, Nong Khai — nationwide coverage",
  "Same-day service for standard notarial acts; 24-hour express legalization available",
  "Direct filing at MFA Chaeng Watthana — no queueing required from clients",
  "Transparent fixed pricing published on-site — VAT invoices issued",
  "On-site service across all 50 Bangkok districts (hotel, condo, corporate office)",
  "Hague Apostille Convention readiness (in force for Thailand 28 February 2027)",
];

export const Route = createFileRoute("/en/services")({
  component: EnServices,
  loader: () => ({ h: (() => {
    const url = absoluteUrl("/en/services", null, "en");
    return {
      meta: [
        { title: ctrTitle(TITLE) },
        { name: "description", content: ctrDesc(DESC) },
        {
          name: "keywords",
          content:
            "notary public thailand, notary public bangkok, certification of copies thailand, document legalization thailand, certified translation thailand, apostille thailand, notarial services attorney",
        },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: SOCIAL_OG_IMAGE },
      ],
      links: [
        { rel: "canonical", href: url },
        ...buildHreflangLinks("/services", null, "en"),
      ],
      scripts: [
        jsonLdScript(
          buildJsonLd([
            organizationSchema(),
            webPageSchema({ url, title: TITLE, description: DESC, speakable: true }),
            breadcrumbListSchema([
              { name: "Home", url: absoluteUrl("/en", null, "en") },
              { name: "Services", url },
            ]),
            legalServiceSchema({
              name: "Thai Notary Law — Notary Public & Legalization Services",
              description: DESC,
              url,
              areaServed: "Thailand",
              serviceType: "Notary Public",
            }),
            faqPageSchema(EN_FAQ.map((f) => ({ question: f.q, answer: f.a }))),
            howToSchema({ name: SERVICES_HOWTO.en.name, description: SERVICES_HOWTO.en.description, totalTime: SERVICES_HOWTO.en.totalTime, steps: SERVICES_HOWTO.en.steps }),
          ]),
        ),
      ],
    };
  })() }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

function EnServices() {
  return (
    <>
      <PageHero
        eyebrow="English · Full Service Catalog"
        title="Notary Public Thailand — Certified Translation & Legalization"
        subtitle="10 categories · 57 individual services · 6 licensed Notarial Services Attorneys · 7 working languages · same-day turnaround in Bangkok."
        primaryCta={{ label: "Call +66-94-895-8999", href: `tel:${SITE.contact.phone}` }}
        secondaryCta={{ label: "Chat on LINE", href: SITE.contact.lineUrl }}
        badges={["Licensed by Lawyers Council of Thailand", "MFA + Embassy Filing", "Same-Day Service"]}
      />

      {/* Answer Summary — AEO block for Google AI Overview / Perplexity */}
      <section className="mx-auto max-w-4xl px-4 pt-10">
        <div
          data-speakable
          className="rounded-xl border border-[var(--gold)]/40 bg-[var(--gold)]/[0.06] p-6"
        >
          <p className="text-xs font-semibold uppercase tracking-widest text-[var(--gold)]">
            Answer summary
          </p>
          <p className="mt-2 text-[15px] leading-relaxed text-foreground">
            Thai Notary Law & Service Co., Ltd. is a Bangkok-based law firm providing{" "}
            <strong>Notary Public, certification of copies, certified translation, and full document legalization</strong>{" "}
            (Notary → MFA → Embassy or Apostille) throughout Thailand. All 6 attorneys are licensed
            Notarial Services Attorneys under the Lawyers Council of Thailand. Fees are quoted per case
            once we know the document type, page count and destination authority — ask us by phone,
            LINE or email. We file directly at the MFA in Chaeng Watthana on your behalf.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12">
        <div className="grid gap-10 lg:grid-cols-3">
          <article className="lg:col-span-2 space-y-6 text-foreground/90">
            <h2 className="text-2xl font-bold text-foreground">About the firm</h2>
            <p className="leading-relaxed">{EN_INTRO}</p>

            <h2 className="text-2xl font-bold text-foreground pt-2">
              Why international clients choose us
            </h2>
            <ul className="grid gap-2 md:grid-cols-2">
              {EN_WHY.map((w) => (
                <li key={w} className="flex gap-2 rounded-lg border border-border bg-card p-3 text-sm">
                  <span className="mt-0.5 text-[var(--gold)]">✓</span>
                  <span>{w}</span>
                </li>
              ))}
            </ul>
          </article>

          <aside className="space-y-4">
            <div className="rounded-xl border border-border bg-card p-5">
              <p className="text-xs uppercase tracking-widest text-[var(--gold)]">Quotation on request</p>
              <ul className="mt-3 space-y-1.5 text-sm">
                <li className="flex justify-between"><span>Signature certification</span><span className="font-semibold">Quotation</span></li>
                <li className="flex justify-between"><span>Certification of copies</span><span className="font-semibold">Quotation</span></li>
                <li className="flex justify-between"><span>Notarised translation</span><span className="font-semibold">Quotation</span></li>
                <li className="flex justify-between"><span>Full MFA + embassy chain</span><span className="font-semibold">Quotation</span></li>
              </ul>
              <a href={`tel:${SITE.contact.phone}`} className="mt-4 block rounded-md bg-[var(--gold)] px-4 py-2.5 text-center text-sm font-semibold text-[var(--primary)]">
                Request a quote
              </a>
            </div>
          </aside>
        </div>
      </section>

      {/* Service categories */}
      <section className="border-y border-border bg-muted/30 py-14">
        <div className="mx-auto max-w-6xl px-4">
          <h2 className="text-2xl font-bold text-foreground">All 10 service categories</h2>
          <ul className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {SERVICE_CATEGORIES.map((c) => (
              <li key={c.slug} className="rounded-xl border border-border bg-card p-5">
                <p className="font-semibold text-foreground">{c.name}</p>
                <p className="mt-1 text-sm text-muted-foreground">{c.tagline}</p>
                <p className="mt-2 text-xs text-[var(--gold)]">{quoteText("en")}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <LocaleDeepContentBlock content={DEEP_CONTENT.en} />

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-4 py-14">
        <h2 className="text-2xl font-bold text-foreground">Frequently asked questions</h2>
        <div className="mt-6 space-y-3">
          {EN_FAQ.map((f) => (
            <details key={f.q} className="group rounded-lg border border-border bg-card p-5">
              <summary className="cursor-pointer list-none font-semibold text-foreground marker:hidden">
                <span className="mr-2 text-[var(--gold)]">Q.</span>
                {f.q}
                <span className="float-right text-[var(--gold)] transition-transform group-open:rotate-45">＋</span>
              </summary>
              <p className="mt-3 border-t border-border pt-3 text-sm leading-relaxed text-foreground/85">
                <span className="mr-2 font-semibold text-[var(--gold)]">A.</span>
                {f.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      {/* Internal link map */}
      <section className="mx-auto max-w-6xl px-4 pb-14">
        <h2 className="text-xl font-bold text-foreground">Related pages</h2>
        <div className="mt-4 flex flex-wrap gap-2 text-sm">
          <Link to="/en" className="rounded-full border border-input px-3 py-1 hover:bg-accent">Home (EN)</Link>
          <Link to="/en/coverage" className="rounded-full border border-input px-3 py-1 hover:bg-accent">Coverage</Link>
          <Link to="/en/about" className="rounded-full border border-input px-3 py-1 hover:bg-accent">About the firm</Link>
          <Link to="/en/contact" className="rounded-full border border-input px-3 py-1 hover:bg-accent">Contact</Link>
          <Link to="/pricing" className="rounded-full border border-input px-3 py-1 hover:bg-accent">Pricing (TH)</Link>
        </div>
      </section>

      <section className="bg-[var(--primary)] py-14 text-[var(--primary-foreground)]">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <p className="text-xs uppercase tracking-widest text-[var(--gold)]">Ready to start</p>
          <h2 className="mt-2 text-3xl font-bold">Free consultation. Transparent quote. No hidden fees.</h2>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href={`tel:${SITE.contact.phone}`} className="rounded-md bg-[var(--gold)] px-6 py-3 text-sm font-semibold text-[var(--primary)]">
              📞 +66-94-895-8999
            </a>
            <a href={SITE.contact.lineUrl} className="rounded-md border border-[var(--gold)] px-6 py-3 text-sm font-semibold text-[var(--gold)]">
              💬 LINE @Thainotary
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
